// URL-query bootstrap: consume callback params from the URL synchronously at module load,
// before any React component mounts.
//
// Backend redirect targets we honor:
//
//   /dashboard?login=success&loginMethod=twitter&accessToken=...&refreshToken=...
//       Twitter OAuth success — persist tokens, redirect to /console.
//
//   /dashboard?linked=success
//   /dashboard?linked=error&message=<reason>
//       OAuth-bind callback (existing user linked X handle). Bootstrap forwards the linked=
//       flag onto /console where the next-mount toast surfaces it.
//
//   /login?error=<reason>
//       Twitter OAuth failure — redirect to /login, surface error via toast on mount.
//
//   /<any>?oauth=success|error
//       Per-key provider OAuth callback — redirect to /console?tab=keys&oauth=...
//
//   /<any>?stripe=success|cancel&session=...
//       Stripe payment return — redirect to /topup?stripe=...
//   /<any>?stripe=auto-recharge-setup-success|auto-recharge-setup-cancel
//       Saved-card setup return — reopen billing's auto top-up dialog.
//
//   /<any>?path=<page>[&k=v…]
//       Generic deep link from older URLs — translated to the new clean URL.
//
//   /bind?apikey=<key>
//       Registration hand-off: stash the apikey for /bind to auto-login on mount, then
//       strip the apikey from the URL bar so it doesn't leak into history/screenshots.
//
// Tokens persist to localStorage. The URL gets rewritten via history.replaceState so the
// SPA router (XShell.RouteProvider) parses it on mount as if the user landed on the clean URL.

import { tokens } from './tokens.js';

const PENDING_ERROR_KEY = 'xapi.pendingError';
// Used by ClaimPage. We don't auto-login at bootstrap time (no React context yet); we just
// stash the key so the page can call AuthProvider.loginWithApiKey() on mount, then clear it.
const PENDING_APIKEY_KEY = 'xapi.pendingApikey';
// Referral / invite code from `?r=<code>` on landing URL. LoginPage reads this same key
// (other.jsx hard-codes the same string) — keep them in sync if either side renames.
// We stash + strip on bootstrap so the user can refresh / navigate without losing it.
const REFERRAL_KEY = 'xapi_referral_code';
const SENSITIVE_QUERY_KEYS = new Set(['accessToken', 'refreshToken', 'apikey']);

// Mirrors the small subset of ROUTES used here. Kept inline to avoid pulling shell.jsx into
// bootstrap (which must run synchronously before React mounts).
const buildCleanUrl = (page, params = {}) => {
  const map = {
    'console':  '/console',
    'login':    '/login',
    'topup':    '/topup',
    'bind':     '/bind',
    'activity': '/activity',
    'register': '/register',
    'workspace':'/workspace',
    'market':   '/market',
    'mcp':      '/mcp',
    'skill':    '/skill',
    'capability':'/capabilities',
    'admin':    '/admin',
    'docs':     '/docs',
    'agent-import': '/agent-import',
    'portal':   '/',
  };
  // Special-case path-param routes — pull the param out of params before query-string serialization.
  let path;
  let remaining = { ...params };
  if (page === 'api-detail' && params.id) {
    path = '/apis/' + encodeURIComponent(params.id);
    delete remaining.id;
  } else if (page === 'capability' && params.id) {
    path = '/capabilities/' + encodeURIComponent(params.id);
    delete remaining.id;
  } else {
    path = map[page] || '/';
  }
  const qs = new URLSearchParams();
  Object.entries(remaining).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, String(v)); });
  const s = qs.toString();
  return path + (s ? '?' + s : '');
};

export function consumeCallbackUrl() {
  if (typeof window === 'undefined' || !window.location) return;
  try {
    const u = new URL(window.location.href);
    const sp = u.searchParams;
    const path = u.pathname;

    // ---- Tokens (Twitter / future SSO) ----
    const at = sp.get('accessToken');
    const rt = sp.get('refreshToken');
    if (at) tokens.set(at, rt || undefined);

    // ---- API key auto-login hand-off (used by claim links like /claim/<sid>?apikey=…) ----
    // We can't call loginWithApiKey here (no React context), so stash and let ClaimPage pick up.
    const apikey = sp.get('apikey');
    if (apikey) {
      try { sessionStorage.setItem(PENDING_APIKEY_KEY, apikey); } catch {}
    }

    // ---- Referral / invite code (?r=<code>) — stash so LoginPage's referral field picks it up
    // when the user later navigates to /login. We do NOT clear it here; LoginPage clears the
    // sessionStorage entry only after a successful QuickStart consumes it (other.jsx:1533).
    // Subsequent visits with a different ?r= will overwrite; visits without ?r= keep the
    // previously stashed code so the user doesn't lose it just by refreshing.
    const ref = sp.get('r');
    if (ref) {
      try { sessionStorage.setItem(REFERRAL_KEY, ref); } catch {}
    }

    // Decide where to land after consuming the callback. null = no redirect needed (URL is fine).
    let redirect = null;

    if (path === '/login' && sp.get('error')) {
      try { sessionStorage.setItem(PENDING_ERROR_KEY, sp.get('error')); } catch {}
      redirect = buildCleanUrl('login');
    } else if (sp.get('linked') === 'success' || sp.get('linked') === 'error' || sp.get('linked') === 'merged') {
      // OAuth-bind callback. Forward the flag (and any error message) into /console where the
      // next mount picks it up and surfaces a toast. We strip everything else from the URL.
      // For 'merged', the new accessToken/refreshToken is already consumed above by tokens.set(at, rt),
      // so the user is now logged in as the ENTITY account that absorbed the virtual one.
      const params = { tab: 'overview', linked: sp.get('linked') };
      if (sp.get('message')) params.message = sp.get('message');
      redirect = buildCleanUrl('console', params);
    } else if (path === '/dashboard' || sp.get('login') === 'success') {
      redirect = buildCleanUrl('console', { tab: 'overview', loginMethod: sp.get('loginMethod') || undefined });
    } else if (sp.get('oauth')) {
      redirect = buildCleanUrl('console', { tab: 'keys', oauth: sp.get('oauth') });
    } else if (sp.get('stripe')) {
      const stripeStatus = sp.get('stripe');
      redirect = stripeStatus === 'auto-recharge-setup-success' || stripeStatus === 'auto-recharge-setup-cancel'
        ? buildCleanUrl('console', { tab: 'billing', autoRecharge: stripeStatus === 'auto-recharge-setup-success' ? 'setup-success' : 'setup-cancel', setupSession: stripeStatus === 'auto-recharge-setup-success' ? sp.get('session') || undefined : undefined })
        : buildCleanUrl('topup', { stripe: stripeStatus, session: sp.get('session') || '' });
    } else if (path === '/bind' && apikey) {
      // /bind?apikey=… — apikey already stashed above; strip from URL.
      redirect = buildCleanUrl('bind');
    } else if (sp.get('path')) {
      // Legacy ?path=foo&k=v deep links — translate to clean URL.
      const params = {};
      sp.forEach((v, k) => {
        if (k !== 'path' && !SENSITIVE_QUERY_KEYS.has(k)) params[k] = v;
      });
      redirect = buildCleanUrl(sp.get('path'), params);
    }

    if (redirect) {
      try { history.replaceState(null, '', redirect + u.hash); } catch {}
    } else if (ref) {
      // No redirect branch fired but we stashed a referral — strip `?r=` from the visible URL
      // so refresh doesn't replay (already stashed) and so the user copy-pasting the URL
      // doesn't accidentally re-tag their friends with the same code.
      try {
        sp.delete('r');
        const cleaned = path + (sp.toString() ? '?' + sp.toString() : '') + u.hash;
        history.replaceState(null, '', cleaned);
      } catch {}
    }
  } catch { /* swallow */ }
}

// Read + clear the one-shot apikey stashed during bootstrap. ClaimPage calls this on mount.
export function takePendingApikey() {
  try {
    const v = sessionStorage.getItem(PENDING_APIKEY_KEY);
    if (v) sessionStorage.removeItem(PENDING_APIKEY_KEY);
    return v || null;
  } catch { return null; }
}

// Read + clear a one-shot error message from sessionStorage. Used by LoginPage on mount.
export function takePendingError() {
  try {
    const v = sessionStorage.getItem(PENDING_ERROR_KEY);
    if (v) sessionStorage.removeItem(PENDING_ERROR_KEY);
    return v || null;
  } catch { return null; }
}

// Run synchronously at module init (imported by api/index.js → main.jsx).
consumeCallbackUrl();
